import { cents, computeLineAmount, roundHalfUpCents, type LineDiscount } from '../money';
import { sendRefusal, type MaterialChange } from './approvalGate';
import { documentNumberText } from './versioning';
import type { DiscountLevelValue } from './settings';
import type { ApprovalStatusValue, QuoteStatusValue } from './statuses';
import { counted } from '../composites/countedUnit';

/**
 * The Quote's own rules (piece 22; decisions 0002, 0012, 0035, 0043, 0087,
 * 0135, 0172): the footer's one primary action per status, the expiry default,
 * the bill-to and ship-to auto-population, the tax-exemption snapshot, the
 * commission-split flag, what a Duplicate copies, which fields are content and
 * which are workflow state, the reopen-as-next-revision verdict, and the
 * material changes an edit hands the approval gate. The service in apps/api
 * reads rows and writes them; nothing here touches a table.
 */
export const QUOTE_EXPIRY_DAYS = 30;

/** `expiryDate` defaults to `quoteDate` + 30 days, and is what the Expired derivation reads. */
export function defaultExpiryDate(quoteDate: Date): Date {
  return new Date(quoteDate.getTime() + QUOTE_EXPIRY_DAYS * 86_400_000);
}

/** 🔒 `openSalesOrder` stands in Convert's place on an Accepted Quote a live Sales Order already names (T438; decision 1121). */
export type QuotePrimaryAction = 'send' | 'markAccepted' | 'extendExpiry' | 'convert' | 'openSalesOrder';

export interface QuoteFooter {
  /** The one primary action the status decides; null on Declined and Void. */
  primary: QuotePrimaryAction | null;
  /** The reason the primary is disabled, on the control; null where it is live. */
  disabledReason: string | null;
  /** Duplicate is offered in every status, Void and Declined included. */
  offersDuplicate: true;
  /** Mark as Declined records the customer's answer, offered while the customer can still answer. */
  offersDecline: boolean;
  /** Void records the company withdrawing the Quote; never one control with the customer's answer. */
  offersVoid: boolean;
}

/**
 * 🔒 The footer carries one primary action, and the status decides which
 * (product owner, 2026-09-14): Draft sends, Sent and ClientViewed mark
 * accepted, Expired extends the expiry, Accepted converts. Send is refused
 * while the gate holds the Quote, with the reason on the disabled control.
 * 🔒 An Accepted Quote a live Sales Order already names opens that order
 * rather than offering Convert (T438; decision 1121).
 */
export function quoteFooter(displayed: QuoteStatusValue, approvalStatus: ApprovalStatusValue, converted = false): QuoteFooter {
  const base = { offersDuplicate: true as const, offersVoid: displayed !== 'Void' };
  switch (displayed) {
    case 'Draft':
      // 🔒 A rejected Draft is corrected and resubmitted from its one primary, so Rejected refuses nothing here (decision 1691).
      return { ...base, primary: 'send', disabledReason: approvalStatus === 'Rejected' ? null : sendRefusal({ approvalStatus }), offersDecline: false };
    // Decision 1225: a send the host has not confirmed, or one that bounced, is still a sent Quote awaiting the customer's answer.
    case 'Sent':
    case 'ClientViewed':
    case 'Sending':
    case 'DeliveryFailed':
      return { ...base, primary: 'markAccepted', disabledReason: null, offersDecline: true };
    case 'Expired':
      return { ...base, primary: 'extendExpiry', disabledReason: null, offersDecline: true };
    case 'Accepted':
      return { ...base, primary: converted ? 'openSalesOrder' : 'convert', disabledReason: null, offersDecline: false };
    case 'Declined':
    case 'Void':
      return { ...base, primary: null, disabledReason: null, offersDecline: false };
  }
}

/** Workflow state, excluded from content: a move on any of these never revises (decision 0172). */
export const QUOTE_WORKFLOW_FIELDS = ['status', 'approvalStatus', 'approvedByPersonId', 'approvedAt', 'rejectionReason', 'escalationReason', 'acceptedRevision', 'revision', 'version'] as const;

/** The header fields that appear on the document the customer receives; the lines are content too. */
export const QUOTE_CONTENT_FIELDS = ['companyId', 'billToSiteId', 'shipToSiteId', 'paymentTerms', 'taxExemptState', 'taxExemptCertificateId', 'taxExemptCertificateExpiresOn', 'quoteDate', 'expiryDate', 'displayMode', 'dealId'] as const;

export function isQuoteContentField(field: string): boolean {
  return (QUOTE_CONTENT_FIELDS as readonly string[]).includes(field);
}

export function isQuoteWorkflowField(field: string): boolean {
  return (QUOTE_WORKFLOW_FIELDS as readonly string[]).includes(field);
}

export type ReopenVerdict = 'edit' | 'reopen' | 'frozen';

/**
 * What a content change does by status: a Draft edits in place; a sent
 * revision — Sent, ClientViewed, or Declined and coming back — reopens as the
 * next revision with no prompt (decision 0172); an Accepted revision is frozen,
 * since acceptance names it and the table moves it to Void alone; a Void
 * Quote is frozen too. Duplicate is the way to a new Draft from either.
 */
export function reopenVerdict(status: QuoteStatusValue): ReopenVerdict {
  if (status === 'Draft') return 'edit';
  if (status === 'Sent' || status === 'ClientViewed' || status === 'Declined' || status === 'Expired') return 'reopen';
  return 'frozen';
}

export function frozenReason(status: QuoteStatusValue): string {
  return status === 'Accepted' ? 'an Accepted revision is frozen; Duplicate the Quote to change it, or Void it' : 'a Void Quote is frozen; Duplicate it to start again';
}

/** The writes a reopen makes: back to Draft as the next revision; the base number never moves. */
export function reopenWrites(quote: { revision: number }): { status: 'Draft'; revision: number } {
  return { status: 'Draft', revision: quote.revision + 1 };
}

export interface SiteChoice {
  id: string;
  startDate: Date | string;
  endDate: Date | string | null;
}

export interface SitePopulation {
  billToSiteId: string | null;
  shipToSiteId: string | null;
  /** How the bill-to was chosen, for the page to say so. */
  billToVia: 'defaultBillingSite' | 'onlySite' | 'oldestOpenSite' | 'none';
}

const open = (site: SiteChoice, at: Date): boolean => {
  const end = site.endDate === null ? null : typeof site.endDate === 'string' ? new Date(site.endDate) : site.endDate;
  return end === null || end.getTime() > at.getTime();
};
const startOf = (site: SiteChoice): number => (typeof site.startDate === 'string' ? new Date(site.startDate) : site.startDate).getTime();

/**
 * 🔨 `billToSiteId` auto-populates from the Company always — its default
 * billing site, else its one open site, else its oldest open site, so a
 * Company with any site always bills somewhere; `shipToSiteId` auto-populates
 * only when the Company has exactly one Site, because picking one of two is a
 * guess that ships material to the wrong door. Both stay editable afterwards.
 */
export function autoPopulateSites(company: { defaultBillingSiteId: string | null; sites: readonly SiteChoice[] }, at: Date = new Date()): SitePopulation {
  const openSites = [...company.sites].filter((s) => open(s, at)).sort((a, b) => startOf(a) - startOf(b) || a.id.localeCompare(b.id));
  const shipToSiteId = openSites.length === 1 ? openSites[0]!.id : null;
  if (company.defaultBillingSiteId && openSites.some((s) => s.id === company.defaultBillingSiteId)) return { billToSiteId: company.defaultBillingSiteId, shipToSiteId, billToVia: 'defaultBillingSite' };
  if (openSites.length === 1) return { billToSiteId: openSites[0]!.id, shipToSiteId, billToVia: 'onlySite' };
  if (openSites.length > 1) return { billToSiteId: openSites[0]!.id, shipToSiteId, billToVia: 'oldestOpenSite' };
  return { billToSiteId: null, shipToSiteId: null, billToVia: 'none' };
}

export interface CertificateChoice {
  id: string;
  effectiveFrom: Date | string;
  /** The expiry the certificate's file holds; null when the file names none. */
  expiresOn: Date | string | null;
  /** Decision 0676: the instant somebody ended it, from which it exempts nothing; absent or null while live. */
  endedAt?: Date | string | null;
}

export interface TaxExemptionSnapshot {
  taxExemptState: boolean;
  taxExemptCertificateId: string | null;
  taxExemptCertificateExpiresOn: Date | null;
}

const asDate = (value: Date | string): Date => (typeof value === 'string' ? new Date(value) : value);

/**
 * The exemption evidence the Quote relies on (decision 0087): the site's
 * certificate in force on the quote date, the latest effective one where
 * several are, with its expiry as it stood then; an ended certificate is in
 * force only before its end (decision 0676). Locked on the Quote and carried
 * forward on conversion without re-verification.
 */
export function taxExemptionSnapshot(certificates: readonly CertificateChoice[], onDate: Date): TaxExemptionSnapshot {
  const inForce = certificates
    .filter((c) => asDate(c.effectiveFrom).getTime() <= onDate.getTime() && (c.expiresOn === null || asDate(c.expiresOn).getTime() >= onDate.getTime()) && (c.endedAt === undefined || c.endedAt === null || asDate(c.endedAt).getTime() > onDate.getTime()))
    .sort((a, b) => asDate(b.effectiveFrom).getTime() - asDate(a.effectiveFrom).getTime());
  const chosen = inForce[0];
  if (!chosen) return { taxExemptState: false, taxExemptCertificateId: null, taxExemptCertificateExpiresOn: null };
  return { taxExemptState: true, taxExemptCertificateId: chosen.id, taxExemptCertificateExpiresOn: chosen.expiresOn === null ? null : asDate(chosen.expiresOn) };
}

/** A ship-to rep other than the Deal owner raises the flag (decision 0135); no ship-to, no rep or no Deal raises nothing. */
export function commissionSplitNeeded(shipToRepPersonId: string | null, dealOwnerPersonId: string | null): boolean {
  return shipToRepPersonId !== null && dealOwnerPersonId !== null && shipToRepPersonId !== dealOwnerPersonId;
}

/** The flag names both reps and the site, so the person deciding the split reads it off the queue. */
export function commissionSplitTitle(facts: { quoteNumber: string; ownerName: string; siteRepName: string; siteLabel: string }): string {
  return `Commission split to decide on ${facts.quoteNumber}: ${facts.siteLabel} resolves to ${facts.siteRepName}, the Deal owner is ${facts.ownerName}`;
}

/** The pair a flag is raised once for: the Deal and the site, whichever Quote raised it. */
export function commissionSplitKey(dealId: string, siteId: string): string {
  return `Deal ${dealId} · Site ${siteId}`;
}

export interface QuoteLineInput {
  /** A stored line's id, kept so an edit updates the row rather than replacing it. */
  id?: string | null;
  itemId: string | null;
  description?: string | null;
  quantity: number;
  unitId?: string | null;
  /** Typed by the rep; absent, the line seeds from the price sheet or the Item (decision 0181). */
  unitPriceCents?: number | null;
  discountType?: 'Percentage' | 'Flat' | null;
  discountValue?: number | null;
  taxCategoryId?: string | null;
  returnable?: boolean | null;
  accountId?: string | null;
  bomLineIds?: readonly string[] | null;
  /** 🔒 The hours a Service Item line's sale budgets, typed beside the quantity (decision 0860); refused on any other line. */
  budgetedHours?: number | null;
  /** 🔒 An allowance the customer authorizes, billed at actuals from the job (decision 2612); a Service deal's quote alone. */
  isAllowance?: boolean | null;
  /** 🔒 A line the customer may take or leave (decision 2650); only the lines taken carry to the Sales Order. */
  isOptional?: boolean | null;
}

/** A line's own checks before anything is priced: a quantity, a description on a free-text row, and a discount that makes sense. */
export function lineProblem(line: QuoteLineInput, index: number): string | null {
  const n = index + 1;
  if (!Number.isFinite(line.quantity) || line.quantity <= 0) return `line ${n}: the quantity is a positive number`;
  if (!line.itemId && !(line.description ?? '').trim()) return `line ${n}: a free-text line carries a description`;
  if (line.unitPriceCents !== undefined && line.unitPriceCents !== null && (!Number.isInteger(line.unitPriceCents) || line.unitPriceCents < 0)) return `line ${n}: a unit price is whole cents at or above zero`;
  return lineDiscountProblem(line, index);
}

/** A line discount that makes sense: a kind the line reads, a value at or above zero, and a percentage no higher than 100. */
export function lineDiscountProblem(line: { discountType?: string | null; discountValue?: number | null }, index: number): string | null {
  const n = index + 1;
  if ((line.discountType ?? null) === null) return null;
  if (line.discountType !== 'Percentage' && line.discountType !== 'Flat') return `line ${n}: a discount is a Percentage or a Flat amount`;
  if (line.discountValue === undefined || line.discountValue === null || !Number.isFinite(line.discountValue) || line.discountValue < 0) return `line ${n}: a discount carries a value`;
  if (line.discountType === 'Percentage' && line.discountValue > 100) return `line ${n}: a percentage discount is between 0 and 100`;
  return null;
}

/** The Discount column exists only while `documents.discountLevel` is Line (decision 0097); a discount typed under Footer is refused rather than dropped. */
export function discountRefusal(level: DiscountLevelValue, lines: readonly QuoteLineInput[]): string | null {
  return carriedDiscountRefusal(level, lines, []);
}

/** A line's discount as a save carries it, the two columns the line stores. */
export interface LineDiscountFields {
  discountType?: string | null;
  discountValue?: number | null;
}

/**
 * 🔒 The Discount Scope setting as every sales document's save reads it
 * (decisions 0097, 1031). Under Footer a line carries no discount it did not
 * already carry: `carried[i]` is the discount line `i` held before this save,
 * from its stored row or from the order line it bills, and keeping it is no
 * typing. 🔨 A new or changed discount under Footer is refused, never dropped.
 */
export function carriedDiscountRefusal(level: DiscountLevelValue, lines: readonly LineDiscountFields[], carried: readonly (LineDiscountFields | null | undefined)[]): string | null {
  if (level === 'Line') return null;
  const typed = lines.some((line, i) => {
    const kind = line.discountType ?? null;
    if (kind === null) return false;
    const held = carried[i];
    return !(held && (held.discountType ?? null) === kind && Number(held.discountValue) === Number(line.discountValue));
  });
  return typed ? 'the Discount Scope setting is Footer, so a line carries no discount; write a footer discount as a line instead' : null;
}

export interface PricedLine {
  quantity: number;
  unitPriceCents: number;
  discount?: LineDiscount | null;
}

export interface QuoteTotals {
  grossCents: number;
  discountCents: number;
  totalCents: number;
  lineCount: number;
}

/** Amount is never stored; the totals sum each line's own once-rounded amount (piece 2, line-item rounding). */
export function quoteTotals(lines: readonly PricedLine[], currencyCode = 'USD'): QuoteTotals {
  let gross = 0;
  let total = 0;
  for (const line of lines) {
    const rate = { amount: line.unitPriceCents, currencyCode };
    gross += computeLineAmount(line.quantity, rate).amount;
    total += computeLineAmount(line.quantity, rate, line.discount ?? undefined).amount;
  }
  return { grossCents: gross, discountCents: gross - total, totalCents: total, lineCount: lines.length };
}

/**
 * The line's discount as the rounding helper reads it; a type the line does not carry is no discount.
 * 🔨 A Flat discount rounds half up to the cent, as the ledger's copies do (T459): `Math.round` made a
 * stored 1.0050 into 100 cents where the posting made it 101, and the tie-out reported the cent.
 */
export function lineDiscountOf(type: string | null | undefined, value: number | null | undefined, currencyCode = 'USD'): LineDiscount | null {
  if (!type || value === null || value === undefined) return null;
  if (type === 'Percentage') return { kind: 'Percentage', percent: value };
  if (type === 'Flat') return { kind: 'Flat', amount: { amount: roundHalfUpCents(value * 100), currencyCode } };
  return null;
}

/** Acceptance names a revision, not a Quote (decision 0012): the writes record which one. */
export function acceptanceWrites(quote: { revision: number }): { status: 'Accepted'; acceptedRevision: number } {
  return { status: 'Accepted', acceptedRevision: quote.revision };
}

export type PortalVerdict = 'accept' | 'decline' | 'superseded' | 'notOpen' | 'expired';

/**
 * 🔒 The customer accepts or declines from the portal, writing the same status
 * a rep writes (product owner, 2026-09-14). An answer naming a revision other
 * than the current one is flagged for a person and bound to neither; an answer
 * to a Quote no longer open, or past its expiry, changes nothing.
 */
export function portalVerdict(quote: { status: QuoteStatusValue; revision: number; expiryDate: Date }, answer: { revision: number; decision: 'accept' | 'decline' }, now: Date): PortalVerdict {
  if (answer.revision !== quote.revision) return 'superseded';
  if (quote.status !== 'Sent' && quote.status !== 'ClientViewed') return 'notOpen';
  if (quote.expiryDate.getTime() < now.getTime() && answer.decision === 'accept') return 'expired';
  return answer.decision;
}

export function supersededAcceptanceTitle(facts: { quoteNumber: string; answeredRevision: number; currentRevision: number }): string {
  return `${facts.quoteNumber}: the customer accepted revision ${String(facts.answeredRevision).padStart(2, '0')}, and revision ${String(facts.currentRevision).padStart(2, '0')} is current; decide which stands`;
}

/** What a Duplicate copies: the lines and the terms into a new Draft; never the status, the sent date, the revision or the acceptance. */
export const DUPLICATED_HEADER_FIELDS = ['companyId', 'billToSiteId', 'shipToSiteId', 'paymentTerms', 'taxExemptState', 'taxExemptCertificateId', 'taxExemptCertificateExpiresOn', 'displayMode', 'dealId'] as const;

export function duplicateHeader<T extends Record<string, unknown>>(source: T): Pick<T, (typeof DUPLICATED_HEADER_FIELDS)[number] & keyof T> {
  const out: Record<string, unknown> = {};
  for (const field of DUPLICATED_HEADER_FIELDS) if (field in source) out[field] = source[field];
  return out as Pick<T, (typeof DUPLICATED_HEADER_FIELDS)[number] & keyof T>;
}

export interface ComparableLine {
  id: string;
  itemId: string | null;
  quantity: number;
  unitPriceCents: number;
  /**
   * 🔒 The money the discount takes off this line, in cents (decision 0539). It
   * was the percentage figure, so a Flat discount of any size compared equal to
   * no discount at all and walked past the gate.
   */
  discountCents: number;
  description: string;
  /** The unit the quantity is in; two of a box and two of a metre are not the same sale. */
  unitId?: string | null;
  /** What the line is taxed as; moving a taxed line to an exempt category moves the customer's total. */
  taxCategoryId?: string | null;
  /** 🔒 Whether the customer may take or leave the line (decision 2650); the mark is content, so moving it reopens a sent revision. */
  isOptional?: boolean;
}

/** The discount a line's own figures take off it, in cents, which is what the comparator compares (decision 0539). */
export function lineDiscountCents(line: { quantity: number; unitPriceCents: number; discountType?: string | null; discountValue?: number | null }): number {
  const discount = lineDiscountOf(line.discountType ?? null, line.discountValue ?? null);
  if (!discount) return 0;
  const gross = cents(roundHalfUpCents(line.quantity * line.unitPriceCents));
  return gross.amount - computeLineAmount(line.quantity, cents(line.unitPriceCents), discount).amount;
}

/**
 * The material changes an edit hands the gate (decision 0192): a price, a
 * quantity or a discount moving on a matched line, a line added or removed, a
 * substitution where the Item changed, a unit or a tax category moving, a
 * Quote line's Optional mark moving (decision 2650), and an incidental change
 * for a description alone. The gate decides whether the
 * approval survives. 🔒 The discount is compared as the money it takes off, in
 * cents, so a Flat discount counts (decision 0539).
 */
export function materialChangesOf(before: readonly ComparableLine[], after: readonly ComparableLine[]): MaterialChange[] {
  const changes: MaterialChange[] = [];
  const byId = new Map(before.map((l) => [l.id, l]));
  const seen = new Set<string>();
  for (const line of after) {
    const prior = byId.get(line.id);
    if (!prior) {
      changes.push({ kind: 'lineAdded' });
      continue;
    }
    seen.add(line.id);
    if (prior.itemId !== line.itemId) changes.push({ kind: 'substitution' });
    if (prior.unitPriceCents !== line.unitPriceCents) changes.push({ kind: 'unitPrice', from: prior.unitPriceCents, to: line.unitPriceCents });
    if (prior.quantity !== line.quantity) changes.push({ kind: 'quantity', from: prior.quantity, to: line.quantity });
    if (prior.discountCents !== line.discountCents) changes.push({ kind: 'discountCents', from: prior.discountCents, to: line.discountCents });
    if ((prior.unitId ?? null) !== (line.unitId ?? null)) changes.push({ kind: 'unit' });
    if ((prior.taxCategoryId ?? null) !== (line.taxCategoryId ?? null)) changes.push({ kind: 'taxCategory' });
    // 🔒 The Optional mark is content (decision 2650): it moves the total the customer reads and the lines they may take.
    if ((prior.isOptional ?? false) !== (line.isOptional ?? false)) changes.push({ kind: 'optional' });
    if (prior.description !== line.description && prior.itemId === line.itemId && prior.unitPriceCents === line.unitPriceCents && prior.quantity === line.quantity && prior.discountCents === line.discountCents) changes.push({ kind: 'incidental' });
  }
  for (const line of before) if (!seen.has(line.id)) changes.push({ kind: 'lineRemoved' });
  return changes;
}

/**
 * 🔒 The changes an edit makes to a document whose approval was carried from
 * the one it was made from (decision 1690): every line billed under a shipping
 * category is set aside on both sides, so a shipping charge added, removed or
 * repriced moves nothing the carried approval reads.
 */
export function carriedChangesOf(before: readonly ComparableLine[], after: readonly ComparableLine[], shippingCategoryIds: ReadonlySet<string>): MaterialChange[] {
  const priced = (line: ComparableLine) => !(line.taxCategoryId && shippingCategoryIds.has(line.taxCategoryId));
  return materialChangesOf(before.filter(priced), after.filter(priced));
}

/** True where the lines differ in anything the customer's copy shows, which is what reopens a sent revision. */
export function linesChanged(before: readonly ComparableLine[], after: readonly ComparableLine[]): boolean {
  return materialChangesOf(before, after).length > 0;
}

/** The subject and body the Send mails, from the document's facts; the PDF rendering is PDF and print layout's and rides along once it exists. */
export function quoteMail(facts: { organizationName: string; quoteNumber: string; revision: number; companyName: string; totalText: string; expiryText: string; lineCount: number; senderName: string; message?: string | null; optionalText?: string | null; linkUrl?: string | null }): { subject: string; body: string; closing: string } {
  const number = documentNumberText('Quote', facts.quoteNumber, facts.revision);
  return {
    subject: `[${number}] Quote from ${facts.organizationName}`,
    body: [
      'Hello,',
      '',
      `Please find quote ${number} from ${facts.organizationName} for ${facts.companyName}: ${counted(facts.lineCount, 'line')}, ${facts.totalText} in total, valid until ${facts.expiryText}.`,
      // 🔒 Decision 2650: the optional lines' own figure, outside the total, where the Quote carries any.
      ...(facts.optionalText ? [`Optional lines of ${facts.optionalText} are listed apart and are not in that total.`] : []),
      // 🔒 Decision 2650: the one link to this send's page, where the customer accepts or declines with no login.
      ...(facts.linkUrl ? ['', `Accept or decline this quote online: ${facts.linkUrl}`] : []),
      ...(facts.message?.trim() ? ['', facts.message.trim()] : []),
    ].join('\n'),
    // 🔒 The sign-off yields to the sender's signature where one is set (decision 0832, amended): the send adds it only where none is.
    closing: [facts.senderName, facts.organizationName].filter((line) => line.trim() !== '').join('\n'),
  };
}
